import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useAuditLog, clearAuditLog, auditToCsv, type AuditEntity } from "@/lib/admin/audit/log";
import { PageHeader } from "@/components/admin/PageHeader";

const ENTITIES: (AuditEntity | "all")[] = ["all", "product", "price", "inventory", "collection", "content", "media", "user", "role", "order", "promotion", "setting", "campaign", "category", "system"];

export const Route = createFileRoute("/admin/audit")({ component: AuditPage });

function AuditPage() {
  const entries = useAuditLog();
  const [entity, setEntity] = useState<AuditEntity | "all">("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => entries.filter((e) =>
    (entity === "all" || e.entity === entity) &&
    (query.trim() === "" || e.summary.toLowerCase().includes(query.toLowerCase()) || (e.entityId ?? "").includes(query))
  ), [entries, entity, query]);

  const exportCsv = () => {
    const blob = new Blob([auditToCsv(filtered)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Audit log" subtitle={`${entries.length} events recorded (last 500)`} action={
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest">Export CSV</button>
          <button onClick={() => confirm("Clear the entire audit log?") && clearAuditLog()} className="border border-red-700/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-red-700">Clear</button>
        </div>
      }/>

      <div className="flex flex-wrap items-center gap-2">
        <select value={entity} onChange={(e) => setEntity(e.target.value as AuditEntity | "all")} className="border border-black/15 px-2 py-2 text-sm">
          {ENTITIES.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search summary or entity id" className="border border-black/15 px-3 py-2 text-sm md:w-96"/>
      </div>

      <div className="overflow-x-auto border border-black/10 bg-white">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left text-[10px] font-semibold uppercase tracking-[0.2em] text-black/50">
              <th className="px-3 py-2">Time</th>
              <th className="px-3 py-2">Actor</th>
              <th className="px-3 py-2">Entity</th>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">Summary</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0
              ? <tr><td colSpan={5} className="px-3 py-6 text-center text-black/40">No events match your filter.</td></tr>
              : filtered.map((e) => (
                  <tr key={e.id} className="border-b border-black/5 align-top">
                    <td className="px-3 py-2 whitespace-nowrap text-black/70">{new Date(e.timestamp).toLocaleString()}</td>
                    <td className="px-3 py-2 text-black/70">{e.actorEmail ?? e.actorId ?? "system"}</td>
                    <td className="px-3 py-2"><span className="border border-black/15 px-2 py-0.5 text-[10px] uppercase tracking-widest text-black/70">{e.entity}</span></td>
                    <td className="px-3 py-2 text-black/70">{e.action}</td>
                    <td className="px-3 py-2 text-black">{e.summary}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
